// Third-party
import React from 'react';

// Components
import PillLabel from '../../misc/PillLabel';
import InfiniteProgressBar from '../../common/InfiniteProgressBar';

// Types
import { DataUploadPortalConfig, DataUploadPortalStatus, DataUploadPortalStep } from '@/types/data-upload-config';

// Utils
import { formatMessage } from '@/utils/i18n';

type Props = {
  value: string;
  original: DataUploadPortalConfig;
  type: string;
  onCancelImport: (type: string, id: string) => void;
};

const StatusCell: React.FunctionComponent<Props> = ({ value, original, type, onCancelImport }: Props) => {
  const handleOnCancel = (id: string) => {
    onCancelImport(type, id);
  };

  const statusCellValue = () => {
    const { dataUpload, key, files } = original;
    const step = dataUpload && dataUpload.step;
    const environment = dataUpload && dataUpload.environment;
    const noStatusData = <i className="icon-minus-med data-empty-icon" />;

    // TODO: enable canCancel from data when it will be available
    // const { workflow } = original;
    // const { canCancel } = workflow;
    const canCancel = false;

    switch (value) {
      case 'undefined':
      case DataUploadPortalStatus.NONE:
        return noStatusData;
      case DataUploadPortalStatus.ONGOING:
        if (!key) return noStatusData;

        let label = formatMessage('Admin.DataUpload.Status.QueuedForProcessing');

        if (step === DataUploadPortalStep.VALIDATING) {
          label = formatMessage('Admin.DataUpload.Status.ValidatingFile', { count: files.length });
        }

        if (step === DataUploadPortalStep.IMPORTING || step === DataUploadPortalStep.IMPORTING_UAT) {
          label = formatMessage('Admin.DataUpload.Status.Importing');
        }

        if (step === DataUploadPortalStep.DONE) {
          return (
            <div className="pill-wrapper success-pill">
              <i className="icon-circle-checkmark" />
              {environment && <PillLabel key={value} className="status-pill" text={formatMessage(`Admin.DataUpload.Status.On${environment}`)} />}
            </div>
          );
        }

        return <InfiniteProgressBar id={key} label={label} canCancel={canCancel} onCancel={id => handleOnCancel(id)} />;
      case DataUploadPortalStatus.SUCCESS:
        return (
          <div className="pill-wrapper success-pill">
            <i className="icon-circle-checkmark-outline" />
            {environment && <PillLabel key={value} className="status-pill" text={formatMessage(`Admin.DataUpload.Status.On${environment}`)} />}
          </div>
        );
      case DataUploadPortalStatus.FAILED:
        return (
          <div className="pill-wrapper error-pill">
            <i className="icon-exclamation-outline" />
            <PillLabel key={value} className="status-pill" text={formatMessage('Admin.DataUpload.Status.ImportError')} />
          </div>
        );
      case DataUploadPortalStatus.CANCELED:
        return (
          <div className="pill-wrapper error-pill">
            <i className="icon-exclamation-outline" />
            <PillLabel key={value} className="status-pill" text={formatMessage('Admin.DataUpload.Status.ImportCanceled')} />
          </div>
        );
      default:
        return value ? <PillLabel key={value} className="status-pill" text={value} /> : noStatusData;
    }
  };

  return statusCellValue();
};

export default StatusCell;
